import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Mail, Sparkles, Check, Gift } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterPopup = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [settings, setSettings] = useState(null);

  // Check if user has made a purchase
  const hasPurchased = () => {
    return localStorage.getItem('brookzone_purchased') === 'true';
  };

  // Check if on admin pages - don't show popup on admin pages
  const isAdminPage = () => {
    return location.pathname.startsWith('/admin');
  };

  // Check if on auth pages - don't show popup on auth pages
  const isAuthPage = () => {
    const authPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/subscribe'];
    return authPaths.some(path => location.pathname.startsWith(path));
  };

  useEffect(() => {
    // Don't show if user has already purchased, on admin pages, or on auth pages
    if (hasPurchased() || isAdminPage() || isAuthPage()) {
      setIsOpen(false);
      return;
    }

    // Fetch settings
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
        
        if (response.data.newsletter_popup_enabled) {
          // Only show popup once per session
          const hasSeenThisSession = sessionStorage.getItem('popup_shown_this_session');
          
          if (!hasSeenThisSession && !isAdminPage() && !isAuthPage()) {
            setTimeout(() => {
              if (!hasPurchased() && !isAdminPage() && !isAuthPage()) {
                setIsOpen(true);
                sessionStorage.setItem('popup_shown_this_session', 'true');
              }
            }, 3000);
          }
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    
    fetchSettings();

    // No interval - popup only shows once per session
    return () => {};
  }, [location.pathname]);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('popup_shown_this_session', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setSubscribed(true);
      toast.success('Thanks for subscribing!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You're already subscribed!");
        setSubscribed(true);
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (hasPurchased() || !isOpen || !settings?.newsletter_popup_enabled) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in" data-testid="newsletter-popup">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal - Slide up from bottom on mobile */}
      <div className="relative bg-card sm:rounded-2xl rounded-t-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 hover:bg-muted rounded-full transition-colors z-10"
          aria-label="Close popup"
          data-testid="popup-close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Decorative header */}
        <div className="bg-gradient-to-r from-primary via-accent to-primary h-2 sm:rounded-t-2xl" />
        
        {/* Animated sparkle background */}
        <div className="absolute top-0 left-0 w-full h-32 overflow-hidden pointer-events-none">
          <div className="absolute top-4 left-8 text-accent/20 animate-pulse">
            <Sparkles className="h-6 w-6" />
          </div>
          <div className="absolute top-12 right-12 text-primary/20 animate-pulse" style={{animationDelay: '0.5s'}}>
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="absolute top-8 left-1/2 text-accent/15 animate-pulse" style={{animationDelay: '1s'}}>
            <Sparkles className="h-5 w-5" />
          </div>
        </div>

        <div className="p-5 sm:p-6 md:p-8 pt-4">
          {!subscribed ? (
            <>
              {/* Icon */}
              <div className="flex justify-center mb-4 sm:mb-5">
                <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-4 sm:p-5 rounded-full">
                  <Gift className="h-10 w-10 sm:h-12 sm:w-12 text-green-600" />
                </div>
              </div>

              {/* Content */}
              <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-center text-foreground mb-2 sm:mb-3">
                Welcome! Get 10% Off
              </h2>
              
              {/* Automatic discount banner */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-5 text-center">
                <p className="text-green-800 font-medium text-sm sm:text-base">
                  🎉 First-time buyers automatically get 10% off at checkout!
                </p>
                <p className="text-green-600 text-xs sm:text-sm mt-1">
                  No code needed - discount applies automatically
                </p>
              </div>

              {/* Optional email subscription */}
              <p className="text-muted-foreground text-center mb-3 sm:mb-4 text-xs sm:text-sm">
                Subscribe to get notified about new arrivals and exclusive deals:
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email (optional)"
                    className="pl-10 h-11 sm:h-12 text-base"
                    data-testid="popup-email-input"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleClose} 
                    className="flex-1 h-11 sm:h-12 order-2 sm:order-1"
                  >
                    Shop Now
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 btn-primary h-11 sm:h-12 order-1 sm:order-2" 
                    disabled={loading || !email}
                    data-testid="popup-submit"
                  >
                    {loading ? '...' : 'Subscribe'}
                  </Button>
                </div>
              </form>

              <p className="text-xs text-muted-foreground text-center mt-3 sm:mt-4 pb-safe">
                No spam, ever. Unsubscribe anytime.
              </p>
            </>
          ) : (
            /* Success state */
            <div className="text-center py-6">
              <div className="flex justify-center mb-5">
                <div className="bg-green-100 p-5 rounded-full">
                  <Check className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <h2 className="font-serif text-2xl font-bold text-foreground mb-3">
                You&apos;re on the list!
              </h2>
              <p className="text-muted-foreground mb-5">
                Thanks for subscribing! Don&apos;t forget - your 10% first-time buyer discount will be applied automatically at checkout.
              </p>
              <Button onClick={handleClose} className="btn-primary h-11">
                Start Shopping
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
